/**
 * Main-thread singleton that manages the Kokoro TTS Web Worker.
 *
 * Usage:
 *   import { kokoroClient } from './kokoro-client';
 *   kokoroClient.preload();                      // start model download
 *   const blob = await kokoroClient.generate({   // synthesize speech
 *     text: 'Hello world',
 *     voice: 'af_heart',
 *     speed: 1.0,
 *   });
 */

// ── Types ────────────────────────────────────────────────────────────

export interface KokoroVoice {
  id: string;
  name: string;
  language: string;
  gender: string;
}

export type KokoroStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PendingRequest {
  resolve: (blob: Blob) => void;
  reject: (err: Error) => void;
}

type StatusListener = (status: KokoroStatus, detail?: string) => void;
type ProgressListener = (loaded: number, total: number, file: string) => void;

// ── Client ───────────────────────────────────────────────────────────

class KokoroClient {
  private worker: Worker | null = null;
  private status: KokoroStatus = 'idle';
  private voices: KokoroVoice[] = [];
  private pending = new Map<string, PendingRequest>();
  private nextId = 0;
  private statusListeners = new Set<StatusListener>();
  private progressListeners = new Set<ProgressListener>();
  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;

  /** Start loading the model in a Web Worker. Safe to call multiple times. */
  preload(): void {
    if (this.worker) return;

    this.status = 'loading';
    this.notifyStatus('loading');

    this.readyPromise = new Promise<void>(resolve => {
      this.readyResolve = resolve;
    });

    this.worker = new Worker(
      new URL('./kokoro-worker.ts', import.meta.url),
      {type: 'module'},
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;

      switch (msg.type) {
        case 'status':
          this.status = 'loading';
          this.notifyStatus('loading', `${msg.device}/${msg.dtype}`);
          break;

        case 'progress':
          this.notifyProgress(msg.loaded, msg.total, msg.file);
          break;

        case 'ready':
          this.status = 'ready';
          this.voices = this.parseVoices(msg.voices);
          this.notifyStatus('ready');
          this.readyResolve?.();
          break;

        case 'generated': {
          const req = this.pending.get(msg.id);
          if (req) {
            this.pending.delete(msg.id);
            req.resolve(msg.blob);
          }
          break;
        }

        case 'error': {
          if (msg.id) {
            const req = this.pending.get(msg.id);
            if (req) {
              this.pending.delete(msg.id);
              req.reject(new Error(msg.error));
            }
          } else {
            this.status = 'error';
            this.notifyStatus('error', msg.error);
          }
          break;
        }
      }
    };

    this.worker.onerror = (err: ErrorEvent) => {
      console.error('[Kokoro] Worker error:', err.message);
      this.status = 'error';
      this.notifyStatus('error', err.message);
    };
  }

  /** Wait until the model is loaded. Calls preload() if not started. */
  async waitReady(): Promise<void> {
    if (this.status === 'ready') return;
    if (!this.readyPromise) this.preload();
    return this.readyPromise!;
  }

  /** Generate speech. Returns a WAV Blob. */
  async generate(opts: {
    text: string;
    voice: string;
    speed?: number;
  }): Promise<Blob> {
    await this.waitReady();

    const id = String(++this.nextId);
    return new Promise<Blob>((resolve, reject) => {
      this.pending.set(id, {resolve, reject});
      this.worker!.postMessage({
        type: 'generate',
        id,
        text: opts.text,
        voice: opts.voice,
        speed: opts.speed ?? 1,
      });
    });
  }

  /** Get the current status. */
  getStatus(): KokoroStatus {
    return this.status;
  }

  /** Get available voices (populated after 'ready'). */
  getVoices(): KokoroVoice[] {
    return this.voices;
  }

  /** Subscribe to status changes. Returns an unsubscribe function. */
  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /** Subscribe to download progress. Returns an unsubscribe function. */
  onProgress(listener: ProgressListener): () => void {
    this.progressListeners.add(listener);
    return () => this.progressListeners.delete(listener);
  }

  /** Terminate the worker and reset state. */
  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.status = 'idle';
    this.voices = [];
    this.readyPromise = null;
    this.readyResolve = null;
    for (const req of this.pending.values()) {
      req.reject(new Error('Worker terminated'));
    }
    this.pending.clear();
  }

  // ── Private ──────────────────────────────────────────────────────

  private notifyStatus(status: KokoroStatus, detail?: string) {
    for (const fn of this.statusListeners) fn(status, detail);
  }

  private notifyProgress(loaded: number, total: number, file: string) {
    for (const fn of this.progressListeners) fn(loaded, total, file);
  }

  private parseVoices(raw: any): KokoroVoice[] {
    // kokoro-js voices is an array of objects or a map. Normalize.
    if (!raw) return [];

    // If it's an array of {id, name, ...}
    if (Array.isArray(raw)) {
      return raw.map((v: any) => ({
        id: v.id ?? v.name ?? '',
        name: v.name ?? v.id ?? '',
        language: v.language ?? this.guessLanguage(v.id ?? ''),
        gender: v.gender ?? this.guessGender(v.id ?? ''),
      }));
    }

    // If it's a Record<string, {...}> or just a list of voice IDs
    if (typeof raw === 'object') {
      return Object.entries(raw).map(([id, info]: [string, any]) => ({
        id,
        name: typeof info === 'string' ? info : info?.name ?? id,
        language: info?.language ?? this.guessLanguage(id),
        gender: info?.gender ?? this.guessGender(id),
      }));
    }

    return [];
  }

  private guessLanguage(id: string): string {
    if (id.startsWith('af_') || id.startsWith('am_')) return 'American';
    if (id.startsWith('bf_') || id.startsWith('bm_')) return 'British';
    return 'English';
  }

  private guessGender(id: string): string {
    if (id.includes('_')) {
      const prefix = id.split('_')[0];
      if (prefix.endsWith('f')) return 'Female';
      if (prefix.endsWith('m')) return 'Male';
    }
    return 'Unknown';
  }
}

/** Global singleton. */
export const kokoroClient = new KokoroClient();
